import {
  AMPLITUDE_ACTIONS,
  AMPLITUDE_CONTACT_TYPES,
} from '@/modules/core/amplitude/events';
import {
  LANG_EN,
  LANG_FR,
} from '@/modules/core/i18n/i18n';
import {
  Amplitude,
  LogOnMount,
} from '@amplitude/react-amplitude';
import Icon from '@/common/components/dataDisplay/Icon';
import { css } from '@emotion/react';
import {
  Contact,
  Mail,
  MapPin,
  Phone,
} from 'lucide-react';
import includes from 'lodash.includes';
import size from 'lodash.size';
import React, { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { LogEvent } from '@/modules/core/amplitude/types/Amplitude';
import { ContactSlider } from '@/modules/core/data/types/ContactSlider';
import { GenericObject } from '@/modules/core/data/types/GenericObject';
import Btn from '@/components/dataDisplay/Btn';
import SpoilerLink from '@/components/dataDisplay/SpoilerLink';
import Tooltip from '@/components/dataDisplay/Tooltip';
import EnglishHybridFlag from '@/components/countryFlags/EnglishHybridFlag';
import FrenchFlag from '@/components/countryFlags/FrenchFlag';

type Props = {
  contact: ContactSlider;
  mode?: 'minimal' | 'full';
}

const SolutionContactCard: React.FunctionComponent<Props> = (props) => {
  const {
    contact,
    mode = 'minimal',
  } = props;
  const { t } = useTranslation();
  const isModeFull = mode === 'full';

  return (
    <Amplitude
      eventProperties={(inheritedProps): GenericObject => ({
        ...inheritedProps,
      })}
    >
      {({ logEvent }: { logEvent: LogEvent }): JSX.Element => (
        <Fragment>
          <div
            className={'contact'}
            css={css`
              .contact-identity {
                font-weight: bold;
              }

              .contact-identity,
              .contact-title,
              .contact-office-label {
                cursor: default;
              }

              .contact-title {
                display: flex;
                flex-direction: row;
                justify-content: space-between;
                width: 100%;
              }

              .flag-icon {
                margin-left: 10px;
              }

              .app-icon {
                cursor: default;
              }
            `}
          >
            <div className={'contact-title'}>
              {
                contact?.name && (
                  <p
                    className={'contact-identity'}
                  >
                    <Icon icon={Contact} variant="light" />
                    {contact?.name}
                  </p>
                )
              }
              {
                !!size(contact?.languages) && (
                  <p
                    className={'contact-languages'}
                  >
                    {
                      includes(contact?.languages, LANG_EN) && (
                        <Tooltip overlay={<span>Speaks English</span>}>
                          <EnglishHybridFlag
                            className={'flag-icon'}
                          />
                        </Tooltip>
                      )
                    }
                    {
                      includes(contact?.languages, LANG_FR) && (
                        <Tooltip overlay={<span>Parle Français</span>}>
                          <FrenchFlag
                            className={'flag-icon'}
                          />
                        </Tooltip>
                      )
                    }
                  </p>
                )
              }
            </div>
            {
              contact?.title && (
                <p
                  className={'contact-title'}
                >
                  {contact?.title}
                </p>
              )
            }
            {
              contact?.officeLabel && (
                <p
                  className={'contact-office-label'}
                >
                  {contact?.officeLabel}
                </p>
              )
            }
            {
              isModeFull && contact?.officeAddress && (
                <p
                  className={'contact-office-address'}
                >
                  <Icon icon={MapPin} variant="light" />
                  {contact?.officeAddress}
                </p>
              )
            }
            {
              isModeFull && contact?.officeBuildingRoomInfo && (
                <p
                  className={'contact-office-building-room-info'}
                >
                  {contact?.officeBuildingRoomInfo}
                </p>
              )
            }
            {
              isModeFull && contact?.officeDescription && (
                <p
                  className={'contact-office-description'}
                >
                  {contact?.officeDescription}
                </p>
              )
            }
            {
              (contact?.officePhone || contact?.email) && (
                <p
                  className={'contact-connect'} // TODO find better class name
                >
                  {
                    contact?.officePhone && (
                      <SpoilerLink
                        id={contact?.officePhone}
                        previewElement={(
                          <Btn>
                            <Icon icon={Phone} />
                            {t('solutionsPage.solutionCard.contact.phone', 'Téléphone')}
                          </Btn>
                        )}
                        spoilerElement={(
                          <Btn>
                            <LogOnMount
                              eventType={'solutions-contact-opened'}
                              eventProperties={{
                                selection: {
                                  ref: contact?.officePhone,
                                  type: AMPLITUDE_CONTACT_TYPES.PHONE,
                                },
                                action: AMPLITUDE_ACTIONS.CLICK,
                              }}
                            />
                            <Icon icon={Phone} />
                            {contact?.officePhone}
                          </Btn>
                        )}
                        href={`tel:${contact?.officePhone}`}
                      />
                    )
                  }
                  {
                    contact?.email && (
                      <SpoilerLink
                        id={contact?.email}
                        previewElement={(
                          <Btn>
                            <Icon icon={Mail} />
                            {t('solutionsPage.solutionCard.contact.email', 'Email')}
                          </Btn>
                        )}
                        spoilerElement={(
                          <Btn>
                            <LogOnMount
                              eventType={'solutions-contact-opened'}
                              eventProperties={{
                                selection: {
                                  ref: contact?.email,
                                  type: AMPLITUDE_CONTACT_TYPES.EMAIL,
                                },
                                action: AMPLITUDE_ACTIONS.CLICK,
                              }}
                            />
                            <Icon icon={Mail} />
                            {contact?.email}
                          </Btn>
                        )}
                        href={`mailto:${contact?.email}`}
                      />
                    )
                  }
                </p>
              )
            }
          </div>
        </Fragment>
      )}
    </Amplitude>
  );
};

export default SolutionContactCard;
